/**
 * Settings Page
 *
 * Shows current plan and upgrade options.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Badge,
  Divider,
  SkeletonBodyText,
  Icon,
} from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { useApi } from '../providers/ApiProvider';
import type { Shop, BillingPlan } from '../types';

export function SettingsPage() {
  const api = useApi();
  const [shop, setShop] = useState<Shop | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [shopData, plansData] = await Promise.all([
        api.getShopInfo(),
        api.getBillingPlans(),
      ]);
      setShop(shopData);
      setPlans(plansData);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubscribe = async (planId: string) => {
    try {
      setSubscribing(planId);
      const result = await api.subscribeToPlan(planId);
      // Redirect to Shopify billing confirmation
      window.top!.location.href = result.confirmationUrl;
    } catch (err) {
      setError('Fehler beim Abonnieren');
      console.error(err);
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <Page title="Einstellungen">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const planBadge = (planName: string) => {
    switch (planName) {
      case 'free':
        return <Badge>Free</Badge>;
      case 'starter':
        return <Badge tone="info">Starter</Badge>;
      case 'pro':
        return <Badge tone="success">Pro</Badge>;
      case 'business':
        return <Badge tone="success">Business</Badge>;
      case 'enterprise':
        return <Badge tone="attention">Enterprise</Badge>;
      default:
        return null;
    }
  };

  const formatPrice = (price: number) => {
    if (price === -1) return 'Auf Anfrage';
    return `€${price}`;
  };

  const formatLimit = (limit: number) => {
    if (limit === -1) return 'Unbegrenzt';
    return `bis ${limit.toLocaleString('de-DE')} Produkte/Monat`;
  };

  const handleEnterpriseContact = () => {
    window.location.href = 'mailto:enterprise@cp-solutions.at?subject=Enterprise%20Anfrage%20-%20AI%20Katalog%20Optimizer';
  };

  return (
    <Page title="Einstellungen">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Current Plan */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingLg">Aktueller Plan</Text>
                {planBadge(shop?.plan || 'free')}
              </InlineStack>

              <BlockStack gap="200">
                <InlineStack gap="200">
                  <Text as="span" fontWeight="bold">Shop:</Text>
                  <Text as="span">{shop?.shopName}</Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" fontWeight="bold">E-Mail:</Text>
                  <Text as="span">{shop?.email}</Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" fontWeight="bold">Kontingent:</Text>
                  <Text as="span">
                    {shop?.productsOptimized} / {shop?.productsLimit} Produkte optimiert
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Text as="h2" variant="headingLg">Verfügbare Pläne</Text>
        </Layout.Section>

        {/* Plan Cards */}
        <Layout.Section>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
            {plans.map((plan) => (
              <div key={plan.id} style={{ flex: '1', minWidth: '180px', maxWidth: '220px' }}>
                <Card>
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '420px' }}>
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text as="h3" variant="headingMd">{plan.name}</Text>
                        {plan.recommended && <Badge tone="attention">Empfohlen</Badge>}
                      </InlineStack>

                      <BlockStack gap="100">
                        <InlineStack gap="100" blockAlign="baseline">
                          <Text as="span" variant="heading2xl">{formatPrice(plan.price)}</Text>
                          {plan.price >= 0 && plan.price !== -1 && (
                            <Text as="span" variant="bodySm" tone="subdued">/Monat netto</Text>
                          )}
                        </InlineStack>
                        <Text as="p" tone="subdued">
                          {formatLimit(plan.productsLimit)}
                        </Text>
                      </BlockStack>

                      <Divider />

                      <BlockStack gap="200">
                        {plan.features.map((feature, i) => (
                          <InlineStack key={i} gap="200" blockAlign="start" wrap={false}>
                            <div style={{ flexShrink: 0 }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text as="span" variant="bodySm">{feature}</Text>
                          </InlineStack>
                        ))}
                      </BlockStack>
                    </BlockStack>

                    <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                      {plan.id === 'enterprise' ? (
                        <Button
                          variant="primary"
                          onClick={handleEnterpriseContact}
                          fullWidth
                        >
                          Kontakt aufnehmen
                        </Button>
                      ) : (
                        <Button
                          variant={plan.recommended ? 'primary' : 'secondary'}
                          onClick={() => handleSubscribe(plan.id)}
                          loading={subscribing === plan.id}
                          disabled={shop?.plan === plan.id.toLowerCase()}
                          fullWidth
                        >
                          {shop?.plan === plan.id.toLowerCase() ? 'Aktueller Plan' : 'Auswählen'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </Layout.Section>

        {/* Help Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Hilfe & Support</Text>
              <Text as="p" tone="subdued">
                Bei Fragen zu deinem Plan oder der App, kontaktiere uns unter{' '}
                <a href="mailto:support@cp-solutions.at">support@cp-solutions.at</a>
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
